import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, Linkedin, Instagram } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

const Footer = () => {
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    // Scroll to top after navigation
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  return (
    <footer className="py-16 w-full bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-12">
          {/* Your Therapist Section */}
          <div className="md:col-span-2">
            <h3 className="text-2xl font-bold text-white mb-4">Your Therapist</h3>
            <p className="text-gray-400 mb-6 max-w-md leading-relaxed">
              Revolutionerar mental hälsovård genom att kombinera AI-teknik med legitimerad terapeutisk expertis.
            </p>
            <div className="flex space-x-4">
              <button 
                onClick={() => {
                  // Track the click event
                  trackEvent('Contact Link Click', { location: 'footer-icon' });
                  handleNavigation('/contact');
                }}
                className="w-10 h-10 bg-gray-800 hover:bg-red-600 rounded-lg flex items-center justify-center transition-colors"
              >
                <Mail className="w-5 h-5" />
              </button>
              <a 
                href="https://www.linkedin.com/company/yourtherapist-se"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  // Track the click event
                  trackEvent('LinkedIn Link Click', { location: 'footer-icon' });
                }}
                className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/yourtherapistse/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  // Track the click event
                  trackEvent('Instagram Link Click', { location: 'footer-icon' });
                }}
                className="w-10 h-10 bg-gray-800 hover:bg-pink-600 rounded-lg flex items-center justify-center transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              
            </div>
          </div>

          {/* For Clients Column */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">För klienter</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => handleNavigation('/')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Hem
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    // Track the click event
                    trackEvent('Discover Life Themes Button Click', { location: 'footer' });
                    handleNavigation('/livsteman');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Utforska livsteman
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    // Track the click event
                    trackEvent('Start Chat Button Click', { location: 'footer' });
                    handleNavigation('/instant-chat');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Kom igång
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    // Track the click event
                    trackEvent('Blog Link Click', { location: 'footer' });
                    handleNavigation('/blog');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Blog
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    // Track the click event
                    trackEvent('Therapists Link Click', { location: 'footer' });
                    handleNavigation('/therapists');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Våra Terapeuter
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    // Track the click event
                    trackEvent('Login Link Click', { location: 'footer' });
                    handleNavigation('/login');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Logga in
                </button>
              </li>
            </ul>
          </div>

          {/* For Therapists Column */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">För terapeuter</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => {
                    // Track the click event
                    trackEvent('Therapist Landing Link Click', { location: 'footer' });
                    handleNavigation('/therapist-landing');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Terapeutsida
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    handleNavigation('/therapist-landing');
                    setTimeout(() => scrollToSection('benefits'), 100);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Fördelar
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    handleNavigation('/therapist-landing');
                    setTimeout(() => scrollToSection('how-it-works'), 100);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Så fungerar det
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    handleNavigation('/therapist-landing');
                    setTimeout(() => scrollToSection('pricing'), 100);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Avgifter
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('/therapist/signup')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Anslut dig
                </button>
              </li>
              
            </ul>
          </div>

          {/* Legal & Support Column */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Juridik & Support</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => handleNavigation('/privacy')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Integritetspolicy
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('/terms')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Användarvillkor
                </button>
              </li>
              <li>
                <a href="/therapist-terms" className="text-gray-400 hover:text-white transition-colors">
                  Terapeutvillkor
                </a>
              </li>
             <li>
                <button 
                  onClick={() => handleNavigation('/contact')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Kontakt
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    handleNavigation('/help');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Hjälp
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
          <div className="text-gray-400 text-sm mb-4 md:mb-0">
            © 2025 Your Therapist. Alla rättigheter förbehållna.
          </div>
          <div className="text-gray-400 text-sm">
            Legitimerade terapeuter • GDPR-compliant • Säker i Sverige
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
